/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React from 'react';
import { css } from 'aphrodite';
import { ColumnInstance, useTable } from 'react-table';

import { getStyles } from './styles';
import { noop, get, camelCase } from '@utils/lodash';

export interface ColumnConfig {
  Cell: React.FC<{ value: any }>;
  Header: React.SFC<{ column: ColumnInstance<any> }>;
  id: string;
  width: string;
  accessor: (data: any) => any;
}

export namespace Table {
  export interface Props {
    data: any[];
    tableColumns: ColumnConfig[];
    onRowClick?: (column: any) => void;
    'data-it'?: string;
  }
}

export const Table: React.FC<Table.Props> = ({ data, tableColumns, onRowClick, 'data-it': dataIt }) => {
  const styles = getStyles(tableColumns);
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } = useTable({
    columns: tableColumns,
    data,
  });
  return (
    <div className={css(styles.pipelineTableContainer)} id="it-PipelineTable-tableContainer">
      <table {...getTableProps({ className: css(styles.tableBody) })} data-it={dataIt}>
        <thead>
          {headerGroups.map((headerGroup, i) => (
            <tr {...headerGroup.getHeaderGroupProps()} key={i}>
              {headerGroup.headers.map((column, columnIndex) => {
                const isFirstColumn = columnIndex === 0;
                return (
                  <th
                    {...column.getHeaderProps({
                      className: css(styles.tableHeader, isFirstColumn && styles.firstColumnIndent),
                    })}
                    key={column.id}
                  >
                    <div className={css(styles.innerCellWrapper)}>{column.render('Header')}</div>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody className={css(styles.pipelineTableContainer)} {...getTableBodyProps()}>
          {rows.map((row, index) => {
            prepareRow(row);
            const isEvenRow = index % 2 === 0;
            return (
              <tr
                {...row.getRowProps({
                  className: css(
                    styles.tableRowBase,
                    isEvenRow ? styles.tableRowEven : styles.tableRowOdd,
                    !!onRowClick && styles.clickableTableRow,
                  ),
                })}
                data-it={`pipelineRow-${row.original.id}`}
                key={row.original.id}
                onClick={!!onRowClick ? () => onRowClick(row.original) : noop}
              >
                {row.cells.map((cell, cellIndex) => {
                  const isFirstCell = cellIndex === 0;
                  return (
                    <td
                      {...cell.getCellProps({
                        className: css(isFirstCell && styles.firstColumnIndent, get(styles, camelCase(cell.column.id))),
                      })}
                      key={`${row.id}-${cell.column.id}`}
                    >
                      <div className={css(styles.innerCellWrapper)}>{cell.render('Cell')}</div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
